export function buildRecommendationNoteContent(recommendation = {}) {
    const explicitNoteContent = `${recommendation?.noteContent || ""}`.trim()
    const rationale = `${recommendation?.rationale || ""}`.trim()
    const targetLabel = `${recommendation?.targetLabel || ""}`.trim()

    if (explicitNoteContent) {
        return explicitNoteContent
    }

    if (rationale) {
        return buildRecommendationReasonStatement(rationale)
    }

    if (targetLabel) {
        return `This change was made to update ${targetLabel}.`
    }

    return "This change was made to improve the schedule."
}

export function mergeRecommendationNoteContent(existingContent = "", recommendation = {}) {
    return mergeRecommendationNoteStrings(existingContent, buildRecommendationNoteContent(recommendation))
}

export function mergeRecommendationNoteContents(existingContent = "", nextContents = []) {
    return normalizeRecommendationNoteInputs(nextContents).reduce(
        mergeRecommendationNoteStrings,
        `${existingContent || ""}`.trim()
    )
}

export function mergeRecommendationNoteStrings(existingContent = "", nextContent = "") {
    const currentContent = `${existingContent || ""}`.trim()
    const normalizedNextContent = `${nextContent || ""}`.trim()

    if (!currentContent) return normalizedNextContent
    if (!normalizedNextContent) return currentContent
    if (containsRecommendationNote(currentContent, normalizedNextContent)) return currentContent

    if (looksLikeHtmlContent(currentContent)) {
        return `${currentContent}${buildRecommendationParagraph(normalizedNextContent)}`
    }

    return `${currentContent} ${normalizedNextContent}`.trim()
}

function normalizeRecommendationNoteInputs(nextContents = []) {
    const normalized = []

    for (const nextContent of Array.isArray(nextContents) ? nextContents : []) {
        const value = `${nextContent || ""}`.trim()
        if (!value) continue
        if (normalized.some((existing) => containsRecommendationNote(existing, value))) continue
        normalized.push(value)
    }

    return normalized
}

function buildRecommendationReasonStatement(rationale) {
    const statement = stripTrailingSentencePunctuation(rationale)
    if (!statement) return "This change was made to improve the schedule."

    if (startsWithReasonLeadIn(statement)) {
        return `This change was made ${lowercaseFirst(statement)}.`
    }

    return `This change was made to ${lowercaseFirst(statement)}.`
}

function containsRecommendationNote(existingContent, nextContent) {
    return normalizeRecommendationNote(existingContent).includes(normalizeRecommendationNote(nextContent))
}

function normalizeRecommendationNote(value) {
    return `${value || ""}`
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
}

function looksLikeHtmlContent(value) {
    return /<[^>]+>/.test(`${value || ""}`)
}

function buildRecommendationParagraph(content) {
    return `<p>${escapeHtml(content)}</p>`
}

function escapeHtml(value) {
    return `${value || ""}`
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
}

function startsWithReasonLeadIn(value) {
    return /^(to|because|so that|in order to)\b/i.test(`${value || ""}`.trim())
}

function stripTrailingSentencePunctuation(value) {
    return `${value || ""}`.trim().replace(/[.!?]+$/, "")
}

function lowercaseFirst(value) {
    const nextValue = `${value || ""}`.trim()
    if (!nextValue) return nextValue
    return nextValue.charAt(0).toLowerCase() + nextValue.slice(1)
}
